import React from 'react';
import {
    Box,
    Typography,
    Grid,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Alert,
    FormHelperText,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useParams, useNavigate } from 'react-router-dom';
import { validateBr } from 'js-brasil';
import { api } from '../../services/api';
import { maskCPF2, maskPhone } from '../../utils/mask';
import isValidEmail from '../../utils/isValidEmail';
import { isPlanAvailable } from '../../utils/plan';
import { ManagerContext } from '../../context/ManagerContext';
import AppNav from '../../components/appNav';



export default function IndividualSubscriber() {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [isFree, setIsFree] = React.useState(false)
    const { manager } = React.useContext(ManagerContext);
    const [loading, setLoading] = React.useState(false);
    const [loadingCep, setLoadingCep] = React.useState(false);
    const [alert, setAlert] = React.useState({ show: false, type: 'error', message: '' });
    const [plans, setPlans] = React.useState([]);
    const [eventData, setEventData] = React.useState(null);
    const [lastPaymentForm, setLastPaymentForm] = React.useState('');

    const [openModalSegundaVia, setOpenModalSegundaVia] = React.useState(false);
    const [cpfSegundaVia, setCpfSegundaVia] = React.useState('');
    const [loadingSegundaVia, setLoadingSegundaVia] = React.useState(false);
    const [errorSegundaVia, setErrorSegundaVia] = React.useState('');
    const [openPixModal, setOpenPixModal] = React.useState(false);
    const [selectedPaymentForm, setSelectedPaymentForm] = React.useState('');
    const paymentOptions = ['Boleto', 'Pix', 'Transação ou Depósito Bancário']


    const getIndividualValidationSchema = (isSemper, paymentForm) => yup.object({
        document: yup
            .string()
            .test('cpf', 'CPF inválido', value => validateBr.cpf(value || ''))
            .required('CPF é obrigatório'),
        name: yup
            .string()
            .min(4, 'Nome deve ter pelo menos 4 caracteres')
            .required('Nome é obrigatório'),
        birth: yup
            .date()
            .max(new Date(), 'Data de nascimento não pode ser futura')
            .required('Data de nascimento é obrigatória'),
        email: yup
            .string()
            .test('email', 'Email inválido', value => !value || isValidEmail(value))
            .optional(),
        gender: yup
            .string()
            .oneOf(['M', 'F','Outros','S'], 'Gênero é obrigatório')
            .required('Gênero é obrigatório'),
        whatsapp: yup
            .string()
            .min(14, 'WhatsApp deve ter pelo menos 10 dígitos')
            .required('WhatsApp é obrigatório'),
        cep: (isSemper && paymentForm !== 'Pix') ? yup.string() : yup
            .string()
            .matches(/^\d{5}-?\d{3}$/, 'CEP inválido')
            .required('CEP é obrigatório'),
        street: (isSemper && paymentForm !== 'Pix') ? yup.string() : yup
            .string()
            .required('Rua é obrigatória'),
        number: (isSemper && paymentForm !== 'Pix') ? yup.string() : yup
            .string()
            .required('Número é obrigatório'),
        neighborhood: (isSemper && paymentForm !== 'Pix') ? yup.string() : yup
            .string()
            .required('Bairro é obrigatório'),
        city: (isSemper && paymentForm !== 'Pix') ? yup.string() : yup
            .string()
            .required('Cidade é obrigatória'),
        state: (isSemper && paymentForm !== 'Pix') ? yup.string() : yup
            .string()
            .required('Estado é obrigatório'),
        complement: yup.string(),
        plan: yup
            .number()
            .min(1, 'Selecione um tipo de inscrição')
            .required('Tipo de inscrição é obrigatório'),
        payment_form: isSemper ? yup.string()
            .test('is-free', 'Forma de pagamento necessária', function (value) {
                const { path, createError } = this;
                if (isFree) {
                    return true;
                }
                if (!value) {
                    return createError({ path, message: 'Forma de pagamento necessária' });
                }
                return true;
            }) : yup.string()
    });
    // Buscar planos do evento
    React.useEffect(() => {
        const fetchEventPlans = async () => {
            try {
                const { data } = await api.get(`/events/${eventId}?populate=plans`);
                setEventData(data);
                setPlans(data.plans || []);
                if (data.typeForm === 'company') {
                    navigate(`/subscribe/${eventId}`);
                }
            } catch (error) {
                console.error('Erro ao buscar planos:', error);
                setAlert({ show: true, type: 'error', message: 'Erro ao carregar tipos de inscrição' });
            }
        };

        if (eventId) {
            fetchEventPlans();
        }
    }, [eventId]);

    const isSemper = manager?.fantasy === 'SEMPER';

    const formik = useFormik({
        initialValues: {
            document: '',
            name: '',
            birth: '',
            email: '',
            gender: '',
            whatsapp: '',
            cep: '',
            street: '',
            number: '',
            neighborhood: '',
            city: '',
            state: '',
            complement: '',
            plan: '',
            payment_form: '',
        },
        validationSchema: getIndividualValidationSchema(isSemper, selectedPaymentForm),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                // Submeter inscrição individual
                const { data } = await api.post('/order/individual', {
                    data: {
                        ...values,
                        event: eventId
                    }
                });
                console.log(data)

                    // armazenar forma de pagamento escolhida para exibir instruções quando necessário
                    setLastPaymentForm(values.payment_form);

                // Se for pagamento Pix, abre modal com checkout
                if (values.payment_form === 'Pix' && data && data.payment_url) {
                    window.open(`${data.payment_url}`, '_blank');
                } else if (data && data.payment_url) {
                    // Para outros tipos de pagamento, abre em nova aba
                    window.open(`${data.payment_url}`, '_blank');
                }

                setAlert({
                    show: true,
                    type: 'success',
                    message: 'Inscrição realizada com sucesso!'
                });


            } catch (error) {
                console.error('Erro ao realizar inscrição:', error);
                setAlert({
                    show: true,
                    type: 'error',
                    message: error.response?.data?.error?.message || 'Erro ao realizar inscrição'
                });
            }
            setLoading(false);
        }
    });

    const handleOpenModalSegundaVia = () => {
        setOpenModalSegundaVia(true);
        setCpfSegundaVia('');
        setErrorSegundaVia('');
    };

    const handleCloseModalSegundaVia = () => {
        setOpenModalSegundaVia(false);
        setCpfSegundaVia('');
        setErrorSegundaVia('');
        setLoadingSegundaVia(false);
    };

    const handleClosePixModal = () => {
        setOpenPixModal(false);
    };
    const handleOpenPixModal = () => {
        setOpenPixModal(true);
        setCpfSegundaVia('');
        setErrorSegundaVia('');
        setLoadingSegundaVia(false);
    }

    const handleBuscarBoletoSegundaVia = async () => {
        // Validar CPF
        if (!validateBr.cpf(cpfSegundaVia)) {
            setErrorSegundaVia('CPF inválido');
            return;
        }

        setLoadingSegundaVia(true);
        setErrorSegundaVia('');

        try {
            // Buscar boleto pendente pelo CPF
            const { data } = await api.get(`/order/individual/${cpfSegundaVia}/${eventId}`);

            if (data && data.payment_url) {
                window.open(data.payment_url, '_blank');
                handleCloseModalSegundaVia();
            } else {
                setErrorSegundaVia('Nenhum boleto pendente encontrado para este CPF.');
            }
        } catch (error) {
            console.error('Erro ao buscar boleto:', error);
            setErrorSegundaVia(error.response?.data?.error?.message || 'Erro ao buscar boleto. Tente novamente.');
        } finally {
            setLoadingSegundaVia(false);
        }
    };

    // Buscar endereço pelo CEP
    const handleCepBlur = async () => {
        const cep = formik.values.cep.replace(/\D/g, '');
        if (cep.length === 8) {
            setLoadingCep(true);
            try {
                const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
                const data = await response.json();

                if (!data.erro) {
                    formik.setFieldValue('street', data.logradouro);
                    formik.setFieldValue('neighborhood', data.bairro);
                    formik.setFieldValue('city', data.localidade);
                    formik.setFieldValue('state', data.uf);
                } else {
                    setAlert({ show: true, type: 'error', message: 'CEP não encontrado' });
                }
            } catch (error) {

            }
            setLoadingCep(false);
        }
    };

    // Auto-preenchimento por CPF
    const handleCpfBlur = async () => {
        if (validateBr.cpf(formik.values.document)) {
            try {
                const { data } = await api.post('/subscriber/form', {
                    data: { cpf: formik.values.document }
                });

                if (data && data.name) {
                    formik.setFieldValue('name', data.name || '');
                    formik.setFieldValue('birth', data.birth || '');
                    formik.setFieldValue('gender', data.gender || '');
                    formik.setFieldValue('whatsapp', data.whatsapp || '');
                }
            } catch (error) {
                console.log('Inscrito não encontrado, continuando com formulário em branco');
            }
        }
    };


    return (
        <Box sx={{ width: "100vw", minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
            <AppNav />
            <Box sx={{ maxWidth: 800, margin: "auto", padding: 4 }}>
                <Typography variant="h4" align="center" gutterBottom fontWeight={700} color="#004D91">
                    {eventData && `${eventData.name}`} <br /> {isSemper ? 'Convite | Ingresso | Inscrição ' : 'Inscrição Individual'}
                </Typography>

                {/* Botão 2ª Via do Boleto */}
                {isSemper && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={handleOpenModalSegundaVia}
                        >
                            2ª Via do Boleto
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleOpenPixModal}
                        >
                            Continuar processamento Pix
                        </Button>
                    </Box>
                )}

                {alert.show && (
                    <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert({ ...alert, show: false })}>
                        {alert.message}
                    </Alert>
                )}
                {alert.show && alert.type === 'success' && lastPaymentForm === 'Transação ou Depósito Bancário' && manager && (
                    <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                        <Typography variant="subtitle2">Dados para Transação/Depósito:</Typography>
                        <Typography variant="body2">{(manager.social_reason || '').toString().toUpperCase()} - {manager.fantasy}</Typography>
                        <Typography variant="body2">{(manager.bank || '').toString().toUpperCase()}</Typography>
                        <Typography variant="body2">AGÊNCIA {manager.agency} &nbsp; OPERAÇÃO {manager.operation} &nbsp; CONTA {manager.cc}</Typography>
                        <Typography variant="body2">CNPJ {manager.document}</Typography>
                        <Typography variant="body2">Enviar comprovante para: <a href={`mailto:${manager.email}`}>{manager.email}</a> ou WhatsApp <a href={`https://api.whatsapp.com/send?phone=55${manager.whatsapp}`}>{manager.whatsapp}</a></Typography>
                    </Box>
                )}
                {!(alert.show && alert.type === 'success') && (
                    <Paper elevation={3} sx={{ padding: 4 }}>
                        <form onSubmit={formik.handleSubmit}>
                            <Grid container spacing={3}>

                                {/* Dados Pessoais */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom color="#004D91">
                                        Dados Pessoais
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="document"
                                        label="CPF"
                                        fullWidth
                                        value={formik.values.document}
                                        onChange={(e) => formik.setFieldValue('document', maskCPF2(e.target.value))}
                                        onBlur={handleCpfBlur}
                                        error={formik.touched.document && Boolean(formik.errors.document)}
                                        helperText={formik.touched.document && formik.errors.document}
                                        inputProps={{ maxLength: 14 }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="name"
                                        label="Nome Completo"
                                        fullWidth
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        error={formik.touched.name && Boolean(formik.errors.name)}
                                        helperText={formik.touched.name && formik.errors.name}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        name="birth"
                                        label="Data de Nascimento"
                                        type="date"
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        value={formik.values.birth}
                                        onChange={formik.handleChange}
                                        error={formik.touched.birth && Boolean(formik.errors.birth)}
                                        helperText={formik.touched.birth && formik.errors.birth}
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <FormControl fullWidth error={formik.touched.gender && Boolean(formik.errors.gender)}>
                                        <InputLabel>{isSemper ? 'Gênero' : 'Sexo'}</InputLabel>
                                        <Select
                                            name="gender"
                                            value={formik.values.gender}
                                            onChange={formik.handleChange}
                                            label={isSemper ? 'Gênero' : 'Sexo'}
                                        >
                                            <MenuItem value="M">Masculino</MenuItem>
                                            <MenuItem value="F">Feminino</MenuItem>
                                            <MenuItem value="S">Não informar</MenuItem>
                                            <MenuItem value="Outros">Outros</MenuItem>
                                            
                                        </Select>
                                        <FormHelperText>{formik.touched.gender && formik.errors.gender}</FormHelperText>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        name="email"
                                        label="Email"
                                        type="email"
                                        fullWidth
                                        required={false}
                                        InputLabelProps={{ shrink: true }}
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        error={formik.touched.email && Boolean(formik.errors.email)}
                                        helperText={formik.touched.email && formik.errors.email}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        name="whatsapp"
                                        label="WhatsApp"
                                        fullWidth
                                        value={formik.values.whatsapp}
                                        onChange={(e) => formik.setFieldValue('whatsapp', maskPhone(e.target.value))}
                                        error={formik.touched.whatsapp && Boolean(formik.errors.whatsapp)}
                                        helperText={formik.touched.whatsapp && formik.errors.whatsapp}
                                        inputProps={{ maxLength: 15 }}
                                    />
                                </Grid>

                                {/* Endereço */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom color="#004D91" sx={{ mt: 2 }}>
                                        Endereço
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        name="cep"
                                        label={(isSemper && selectedPaymentForm !== 'Pix') ? "CEP (opcional)" : "CEP"}
                                        fullWidth
                                        required={!isSemper || selectedPaymentForm === 'Pix'}
                                        value={formik.values.cep}
                                        onChange={(e) => formik.setFieldValue('cep', e.target.value.replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2'))}
                                        onBlur={handleCepBlur}
                                        error={formik.touched.cep && Boolean(formik.errors.cep)}
                                        helperText={formik.touched.cep && formik.errors.cep}
                                        inputProps={{ maxLength: 9 }}
                                        InputProps={{
                                            endAdornment: loadingCep && <CircularProgress size={20} />
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="street"
                                        label={(isSemper && selectedPaymentForm !== 'Pix') ? "Rua (opcional)" : "Rua"}
                                        fullWidth
                                        required={!isSemper || selectedPaymentForm === 'Pix'}
                                        value={formik.values.street}
                                        onChange={formik.handleChange}
                                        error={formik.touched.street && Boolean(formik.errors.street)}
                                        helperText={formik.touched.street && formik.errors.street}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        name="number"
                                        label={(isSemper && selectedPaymentForm !== 'Pix') ? "Número (opcional)" : "Número"}
                                        fullWidth
                                        required={!isSemper || selectedPaymentForm === 'Pix'}
                                        value={formik.values.number}
                                        onChange={formik.handleChange}
                                        error={formik.touched.number && Boolean(formik.errors.number)}
                                        helperText={formik.touched.number && formik.errors.number}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        name="neighborhood"
                                        label={(isSemper && selectedPaymentForm !== 'Pix') ? "Bairro (opcional)" : "Bairro"}
                                        fullWidth
                                        required={!isSemper || selectedPaymentForm === 'Pix'}
                                        value={formik.values.neighborhood}
                                        onChange={formik.handleChange}
                                        error={formik.touched.neighborhood && Boolean(formik.errors.neighborhood)}
                                        helperText={formik.touched.neighborhood && formik.errors.neighborhood}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        name="city"
                                        label={(isSemper && selectedPaymentForm !== 'Pix') ? "Cidade (opcional)" : "Cidade"}
                                        fullWidth
                                        required={!isSemper || selectedPaymentForm === 'Pix'}
                                        value={formik.values.city}
                                        onChange={formik.handleChange}
                                        error={formik.touched.city && Boolean(formik.errors.city)}
                                        helperText={formik.touched.city && formik.errors.city}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        name="state"
                                        label={(isSemper && selectedPaymentForm !== 'Pix') ? "Estado (opcional)" : "Estado"}
                                        fullWidth
                                        required={!isSemper || selectedPaymentForm === 'Pix'}
                                        value={formik.values.state}
                                        onChange={formik.handleChange}
                                        error={formik.touched.state && Boolean(formik.errors.state)}
                                        helperText={formik.touched.state && formik.errors.state}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        name="complement"
                                        label="Complemento (opcional)"
                                        fullWidth
                                        value={formik.values.complement}
                                        onChange={formik.handleChange}
                                    />
                                </Grid>

                                {/* Tipo de Inscrição */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom color="#004D91" sx={{ mt: 2 }}>
                                        Tipo de Inscrição
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControl fullWidth error={formik.touched.plan && Boolean(formik.errors.plan)}>
                                        <InputLabel>Tipo de Inscrição</InputLabel>
                                        <Select
                                            name="plan"
                                            value={formik.values.plan}
                                            onChange={(e) => {
                                                setIsFree(plans.find(p => p.id === e.target.value)?.price === 0);

                                                formik.handleChange(e);
                                            }}
                                            label="Tipo de Inscrição"
                                        >
                                            {plans.filter(plan => isPlanAvailable(plan)).map((plan) => (
                                                <MenuItem key={plan.id} value={plan.id}>
                                                    {plan.type} - {Intl.NumberFormat('pt-br', { style: 'currency', currency: 'BRL' }).format(plan.price)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>{formik.touched.plan && formik.errors.plan}</FormHelperText>
                                    </FormControl>
                                </Grid>

                                {/* Forma de Pagamento */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom color="#004D91" sx={{ mt: 2 }}>
                                        Forma de Pagamento
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth error={formik.touched.payment_form && Boolean(formik.errors.payment_form)}>
                                        <InputLabel>Forma de Pagamento</InputLabel>
                                        <Select
                                            name="payment_form"
                                            value={formik.values.payment_form}
                                            onChange={(e) => {
                                                setSelectedPaymentForm(e.target.value);
                                                formik.handleChange(e);
                                            }}
                                            label="Forma de Pagamento"
                                        >
                                            <MenuItem disabled={!isFree} value={"Gratuito"} sx={{ display: isFree ? 'block' : 'none' }}>Gratuito</MenuItem>
                                            {!isFree && !!eventData?.payment_options &&
                                                Object.keys(eventData?.payment_options).map((form) => {
                                                    if (eventData?.payment_options[form]) {
                                                        return (
                                                            <MenuItem value={form} sx={{ display: eventData?.payment_options[form] ? 'block' : 'none' }} >{form}</MenuItem>
                                                        )
                                                    }
                                                })
                                            }
                                            {!isFree && !eventData?.payment_options &&
                                                paymentOptions.map((form) => {
                                                    return (
                                                        <MenuItem value={form}>{form}</MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                        <FormHelperText>{formik.touched.plan && formik.errors.plan}</FormHelperText>
                                    </FormControl>
                                </Grid>

                                {/* Botões */}
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => navigate(`/event/${eventId}`)}
                                        >
                                            Voltar
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={loading}
                                            sx={{ minWidth: 120 }}
                                        >
                                            {loading ? <CircularProgress size={24} /> : 'Realizar Inscrição'}
                                        </Button>
                                    </Box>
                                </Grid>

                            </Grid>
                        </form>
                    </Paper>
                )}

            </Box>

            {/* Modal 2ª Via do Boleto */}
            <Dialog open={openModalSegundaVia} onClose={handleCloseModalSegundaVia} maxWidth="sm" fullWidth>
                <DialogTitle>2ª Via do Boleto</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Informe o CPF para buscar o boleto pendente:
                    </Typography>
                    <TextField
                        fullWidth
                        label="CPF"
                        value={cpfSegundaVia}
                        onChange={(e) => setCpfSegundaVia(maskCPF2(e.target.value))}
                        error={Boolean(errorSegundaVia)}
                        helperText={errorSegundaVia}
                        inputProps={{ maxLength: 14 }}
                        autoFocus
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleBuscarBoletoSegundaVia();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModalSegundaVia} disabled={loadingSegundaVia}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleBuscarBoletoSegundaVia}
                        variant="contained"
                        disabled={loadingSegundaVia || !cpfSegundaVia}
                    >
                        {loadingSegundaVia ? <CircularProgress size={24} /> : 'Buscar Boleto'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal Checkout Pix */}
            <Dialog
                open={openPixModal}
                onClose={handleClosePixModal}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Checkout Pix
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Informe o CPF para buscar o boleto pendente:
                    </Typography>
                    <TextField
                        fullWidth
                        label="CPF"
                        value={cpfSegundaVia}
                        onChange={(e) => setCpfSegundaVia(maskCPF2(e.target.value))}
                        error={Boolean(errorSegundaVia)}
                        helperText={errorSegundaVia}
                        inputProps={{ maxLength: 14 }}
                        autoFocus
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleBuscarBoletoSegundaVia();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePixModal} color="primary">
                        Fechar
                    </Button>
                     <Button
                        onClick={handleBuscarBoletoSegundaVia}
                        variant="contained"
                        disabled={loadingSegundaVia || !cpfSegundaVia}
                    >
                        {loadingSegundaVia ? <CircularProgress size={24} /> : 'Continuar Pagamento Pix'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}